import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import {
  checkEmailFormat,
  EMPLOYEE_RANGES,
  FOUNDER_SLOTS,
  PASSWORD_MIN_LENGTH,
  SUPPORT_EMAIL,
  TRADES,
  type InvitationPreview,
} from "@handvero/shared";
import { api, ApiFailure, errorText } from "../api";
import { Field, Notice } from "../components/ui";
import { useStats } from "../hooks";
import { modeFor } from "../mode";

function value(form: HTMLFormElement, name: string): string {
  const el = form.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement | null;
  return el?.value ?? "";
}

export function Registrieren() {
  const navigate = useNavigate();
  const stats = useStats();
  const openedAt = useRef(Date.now());
  const [error, setError] = useState("");
  const [emailHint, setEmailHint] = useState("");
  const [busy, setBusy] = useState(false);

  function precheckEmail(raw: string) {
    if (!raw.trim()) return setEmailHint("");
    const r = checkEmailFormat(raw);
    setEmailHint(r.ok ? "" : r.message);
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = e.currentTarget;
    setError("");
    const email = value(f, "email");
    const pre = checkEmailFormat(email);
    if (!pre.ok) return setError(pre.message);
    if (value(f, "password").length < PASSWORD_MIN_LENGTH) return setError(`Das Passwort braucht mindestens ${PASSWORD_MIN_LENGTH} Zeichen.`);
    const terms = f.elements.namedItem("acceptTerms") as HTMLInputElement;
    if (!terms.checked) return setError("Bitte bestätigen Sie, dass Sie als Unternehmer handeln, und stimmen Sie den Nutzungsbedingungen und der Auftragsverarbeitung zu.");

    setBusy(true);
    try {
      const res = await api.post<{ email: string }>("/api/auth/register", {
        companyName: value(f, "companyName"),
        trade: value(f, "trade") || undefined,
        contactName: value(f, "contactName"),
        email,
        phone: value(f, "phone"),
        password: value(f, "password"),
        employeeRange: value(f, "employeeRange") || undefined,
        acceptTerms: true,
        website: value(f, "website"),
        formMs: Date.now() - openedAt.current,
      });
      navigate("/registrierung-gesendet", { state: { email: res.email } });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const left = stats?.founderSlotsLeft ?? FOUNDER_SLOTS;
  return (
    <section data-page="registrieren">
    <div className="wrap reg-layout">
      <div className="stack">
        <span className="eyebrow">Firmenkonto anlegen</span>
        <h1>Kostenlos registrieren</h1>
        <p className="small muted">Nur für Betriebe, Selbstständige und Freiberufler. Sie sind Privatperson? Dann geht es hier zu <Link to="/privat/registrieren">HandVero Zuhause</Link>.</p>
        <Notice kind="info">
          <b>Demo, Startphase:</b> HandVero ist noch nicht vollständig verfügbar. Sie können sich registrieren und die Website, die Web-App auf dem
          Handy und die Windows-Software nutzen. Bitte sichern Sie Ihre Daten derzeit selbst, <Link to="/stand">mehr dazu</Link>.
        </Notice>
        <Notice kind="info">Wir prüfen jede Registrierung, um Ihre Daten und unsere Kunden zu schützen. Wegwerf-Adressen nehmen wir nicht an.</Notice>
        <form className="form" onSubmit={submit} noValidate>
          <div className="row-2">
            <Field id="companyName" label="Firmenname" autoComplete="organization" required />
            <div className="field">
              <label htmlFor="trade">Gewerk</label>
              <select id="trade" name="trade" defaultValue="">
                <option value="">Bitte wählen</option>
                {TRADES.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>
          <Field id="contactName" label="Ansprechpartner (Vor- und Nachname)" autoComplete="name" required />
          <div className="row-2">
            <Field
              id="email"
              label="Geschäftliche E-Mail"
              type="email"
              autoComplete="email"
              required
              onBlur={(e) => precheckEmail(e.target.value)}
              aria-invalid={emailHint ? true : undefined}
              hint={emailHint || undefined}
            />
            <Field id="phone" label="Telefon" type="tel" autoComplete="tel" required />
          </div>
          <div className="row-2">
            <Field
              id="password"
              label="Passwort"
              type="password"
              autoComplete="new-password"
              required
              minLength={PASSWORD_MIN_LENGTH}
              hint={`Mindestens ${PASSWORD_MIN_LENGTH} Zeichen.`}
            />
            <div className="field">
              <label htmlFor="employeeRange">Anzahl Mitarbeiter (freiwillig)</label>
              <select id="employeeRange" name="employeeRange" defaultValue="">
                <option value="">Keine Angabe</option>
                {EMPLOYEE_RANGES.map((r) => (
                  <option key={r}>{r}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="hp" aria-hidden="true">
            <label htmlFor="website">Webseite</label>
            <input id="website" name="website" tabIndex={-1} autoComplete="off" />
          </div>
          <label className="check-row">
            <input type="checkbox" name="acceptTerms" />
            <span>
              Ich handle als Unternehmer, stimme den <Link to="/rechtliches#agb">Nutzungsbedingungen</Link> und der{" "}
              <Link to="/rechtliches#avv">Auftragsverarbeitung</Link> zu und habe die <Link to="/rechtliches#datenschutz">Datenschutzerklärung</Link>{" "}
              gelesen.
            </span>
          </label>
          <Notice kind="bad">{error}</Notice>
          <button className="btn btn-primary" type="submit" disabled={busy}>
            {busy ? "Wird angelegt …" : "Konto anlegen"}
          </button>
          <p className="small muted">
            Schon registriert? <Link to="/anmelden">Anmelden</Link>
          </p>
        </form>
      </div>
      <div className="stack">
        <div className="card">
          <h3>Was danach passiert</h3>
          <ul className="checks">
            <li>Sie erhalten eine E-Mail mit Bestätigungslink</li>
            <li>Nach der Bestätigung öffnet sich Ihr Kundenbereich</li>
            <li>Dort finden Sie die Web-App für Ihr Handy und den Download der Windows-Software.</li>
          </ul>
        </div>
        {left > 0 && (
          <div className="card">
            <span className="tag go">Startaktion</span>
            <p>
              Noch <b>{left}</b> von {FOUNDER_SLOTS} Startplätzen frei: mindestens 3 Jahre kostenlos mit bis zu 3 Mitarbeitern. Der Platz wird mit
              der Bestätigung Ihrer E-Mail-Adresse vergeben.
            </p>
          </div>
        )}
      </div>
    </div>
    </section>
  );
}

function ResendLink({ email }: { email: string }) {
  const [state, setState] = useState<"idle" | "busy" | "done">("idle");
  async function resend() {
    setState("busy");
    await api.post("/api/auth/resend-verification", { email }).catch(() => undefined);
    setState("done");
  }
  if (state === "done") return <Notice kind="ok">Falls ein unbestätigtes Konto zu {email} gehört, ist ein neuer Link unterwegs.</Notice>;
  return (
    <button className="btn-link" onClick={resend} disabled={state === "busy"}>
      Neuen Bestätigungslink senden
    </button>
  );
}

export function RegistrierungGesendet() {
  const { state } = useLocation() as { state: { email?: string } | null };
  const email = state?.email;
  return (
    <section data-page="auth">
    <div className="wrap narrow stack">
      <span className="eyebrow">Fast geschafft</span>
      <h1>Bitte bestätigen Sie Ihre E-Mail-Adresse</h1>
      <p className="lead">
        Wir haben einen Link an <b>{email ?? "Ihre Adresse"}</b> geschickt. Erst nach der Bestätigung wird Ihr Konto freigeschaltet.
      </p>
      <p className="muted">Keine E-Mail erhalten? Prüfen Sie den Spam-Ordner. Der Link ist 24 Stunden gültig.</p>
      {email && <ResendLink email={email} />}
    </div>
    </section>
  );
}

export function Bestaetigen() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") ?? "";
  const [state, setState] = useState<{ status: "busy" | "error" | "already"; message?: string }>({ status: "busy" });
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!token) return setState({ status: "error", message: "In diesem Link fehlt der Bestätigungscode." });
    api
      .post<{ alreadyVerified?: boolean }>("/api/auth/verify", { token })
      .then((r) => (r.alreadyVerified ? setState({ status: "already" }) : navigate("/kundenbereich?neu=1", { replace: true })))
      .catch((err) => setState({ status: "error", message: errorText(err) }));
  }, [token, navigate]);

  return (
    <section data-page="auth">
    <div className="wrap narrow stack">
      {state.status === "busy" && <h1>Ihre E-Mail-Adresse wird bestätigt …</h1>}
      {state.status === "already" && (
        <>
          <h1>Schon erledigt</h1>
          <p className="lead">Ihre E-Mail-Adresse ist bereits bestätigt. Melden Sie sich an, um in den Kundenbereich zu gelangen.</p>
          <Link className="btn btn-primary" to="/anmelden" style={{ justifySelf: "start" }}>
            Anmelden
          </Link>
        </>
      )}
      {state.status === "error" && (
        <>
          <h1>Das hat nicht geklappt</h1>
          <Notice kind="bad">{state.message}</Notice>
          <ResendForm />
        </>
      )}
    </div>
    </section>
  );
}

function ResendForm() {
  const [email, setEmail] = useState("");
  return (
    <form className="form" onSubmit={(e) => e.preventDefault()}>
      <Field id="resendEmail" label="Ihre E-Mail-Adresse" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      {email.includes("@") && <ResendLink email={email} />}
    </form>
  );
}

/** Nur Ziele auf der eigenen Seite zulassen, damit der Link nicht auf fremde Seiten umleitet. */
function safeTarget(raw: string | null): string {
  return raw && raw.startsWith("/") && !raw.startsWith("//") && !raw.startsWith("/\\") ? raw : "/kundenbereich";
}

export function Anmelden() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const target = safeTarget(params.get("weiter"));
  const [error, setError] = useState<{ message: string; code?: string } | null>(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post("/api/auth/login", { email, password: value(e.currentTarget, "password") });
      navigate(target);
    } catch (err) {
      setError({ message: errorText(err), code: err instanceof ApiFailure ? err.code : undefined });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section data-page="auth">
    <div className="wrap" style={{ maxWidth: 520 }}>
      <div className="card stack" style={{ padding: 36 }}>
        <h1 style={{ fontSize: "2rem" }}>Anmelden</h1>
        <form className="form" onSubmit={submit} noValidate>
          <Field id="email" label="E-Mail" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <Field id="password" label="Passwort" type="password" autoComplete="current-password" required />
          {error && (
            <Notice kind="bad">
              {error.message}
              {error.code === "not_verified" && (
                <div style={{ marginTop: 8 }}>
                  <ResendLink email={email} />
                </div>
              )}
            </Notice>
          )}
          <button className="btn btn-primary" type="submit" disabled={busy}>
            {busy ? "Anmelden …" : "Anmelden"}
          </button>
        </form>
        <p className="small">
          <Link to="/passwort-vergessen">Passwort vergessen?</Link>
        </p>
        <p className="small muted">
          Noch kein Konto? <Link to={modeFor(location.pathname) ? "/privat/registrieren" : "/registrieren"}>Kostenlos registrieren</Link>
        </p>
      </div>
    </div>
    </section>
  );
}

export function PasswortVergessen() {
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError("");
    try {
      await api.post("/api/auth/forgot-password", { email: value(e.currentTarget, "email") });
      setSent(true);
    } catch (err) {
      setError(errorText(err));
    }
  }
  return (
    <section data-page="auth">
    <div className="wrap narrow stack">
      <h1>Passwort vergessen</h1>
      {sent ? (
        <Notice kind="ok">Falls ein Konto zu dieser Adresse gehört, haben wir einen Link zum Zurücksetzen geschickt. Er ist 1 Stunde gültig.</Notice>
      ) : (
        <form className="form" onSubmit={submit} noValidate>
          <p className="lead">Geben Sie Ihre E-Mail-Adresse ein. Wir schicken Ihnen einen Link, mit dem Sie ein neues Passwort vergeben.</p>
          <Field id="email" label="E-Mail" type="email" autoComplete="email" required />
          <Notice kind="bad">{error}</Notice>
          <button className="btn btn-primary" type="submit">
            Link senden
          </button>
        </form>
      )}
    </div>
    </section>
  );
}

export function PasswortNeu() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError("");
    const password = value(e.currentTarget, "password");
    if (password.length < PASSWORD_MIN_LENGTH) return setError(`Das Passwort braucht mindestens ${PASSWORD_MIN_LENGTH} Zeichen.`);
    try {
      await api.post("/api/auth/reset-password", { token: params.get("token") ?? "", password });
      navigate("/kundenbereich");
    } catch (err) {
      setError(errorText(err));
    }
  }
  return (
    <section data-page="auth">
    <div className="wrap narrow stack">
      <h1>Neues Passwort vergeben</h1>
      <form className="form" onSubmit={submit} noValidate>
        <Field id="password" label="Neues Passwort" type="password" autoComplete="new-password" hint={`Mindestens ${PASSWORD_MIN_LENGTH} Zeichen.`} />
        <Notice kind="bad">{error}</Notice>
        <button className="btn btn-primary" type="submit">
          Passwort speichern
        </button>
        <p className="small">
          <Link to="/passwort-vergessen">Neuen Link anfordern</Link>
        </p>
      </form>
    </div>
    </section>
  );
}

export function Einladung() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") ?? "";
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [loadError, setLoadError] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get<InvitationPreview>(`/api/invitations/preview?token=${encodeURIComponent(token)}`)
      .then(setPreview)
      .catch((err) => setLoadError(errorText(err)));
  }, [token]);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError("");
    const f = e.currentTarget;
    try {
      await api.post("/api/invitations/accept", { token, name: value(f, "name"), password: value(f, "password") });
      navigate("/kundenbereich");
    } catch (err) {
      setError(errorText(err));
    }
  }

  if (loadError) {
    return (
      <section data-page="auth">
    <div className="wrap narrow stack">
        <h1>Einladung nicht mehr gültig</h1>
        <Notice kind="bad">{loadError}</Notice>
      </div>
      </section>
    );
  }
  if (!preview) return <section data-page="auth" />;
  return (
    <section data-page="auth">
    <div className="wrap narrow stack">
      <span className="eyebrow">Einladung</span>
      <h1>Willkommen bei {preview.companyName}</h1>
      <p className="lead">Legen Sie Ihren Zugang für {preview.email} an. Danach sind Sie sofort angemeldet.</p>
      <form className="form" onSubmit={submit} noValidate>
        <Field id="name" label="Vor- und Nachname" autoComplete="name" />
        <Field id="password" label="Passwort" type="password" autoComplete="new-password" hint={`Mindestens ${PASSWORD_MIN_LENGTH} Zeichen.`} />
        <Notice kind="bad">{error}</Notice>
        <button className="btn btn-primary" type="submit">
          Zugang anlegen
        </button>
      </form>
      <p className="small muted">Fragen? Schreiben Sie an {SUPPORT_EMAIL}.</p>
    </div>
    </section>
  );
}

/** Erste Anlaufstelle für „Login“ in der Navigation: Wer ein Konto hat, meldet sich an, wer neu ist, registriert sich. */
export function LoginWahl() {
  return (
    <section data-page="auth">
      <div className="wrap" style={{ maxWidth: 760 }}>
        <div className="stack" style={{ gap: 20 }}>
          <h1 style={{ fontSize: "2rem" }}>Willkommen bei HandVero</h1>
          <div className="grid-2">
            <div className="card stack">
              <h2 style={{ fontSize: "1.3rem" }}>Ich habe schon ein Konto</h2>
              <p>Melden Sie sich mit Ihrer E-Mail-Adresse und Ihrem Passwort an.</p>
              <Link className="btn btn-primary" to="/anmelden">
                Anmelden
              </Link>
            </div>
            <div className="card stack">
              <h2 style={{ fontSize: "1.3rem" }}>Ich bin neu hier</h2>
              <p>Legen Sie Ihre Firma an. Das dauert wenige Minuten und kostet nichts.</p>
              <Link className="btn btn-ghost" to="/registrieren">
                Registrieren
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
